/*
  Fit Finder page quiz (T-11). Progressive enhancement: the styles grid, comparison
  table and sizing rules are always in the server-rendered HTML (crawlable, works
  with JS off). This only reveals the interactive quiz on top and resolves a match.

  The one rule that matters: NECKLINE OVERRIDES GOAL. A backless outfit is a hard
  constraint; a shaping goal is a preference. The matrix encodes that.
*/
class BloomFitGuide extends HTMLElement {
  connectedCallback() {
    this.quiz = this.querySelector('[data-quiz]');
    if (!this.quiz) return;

    try {
      this.products = JSON.parse(this.dataset.products || '{}');
    } catch (e) {
      return; // leave the crawlable fallback in place if the data is malformed
    }

    this.why = {
      tummy: 'Targeted control through the midsection and lower belly in a brief cut, with no straps to work around your neckline.',
      fullbody: 'Continuous head-to-hip coverage with no waistband edge to show or roll. The most forgiving option if your waist and hip measurements are far apart.',
      underbust: 'It starts below the bust, so you wear your own bra and there is nothing to show at the back. The right choice for anything low-back, backless or deep-necked.',
      shorts: 'Padded lift through the seat plus mid-thigh smoothing — the one to choose if chafing or thigh coverage matters as much as shaping.',
    };

    // [main, alternate] — 'backless' overrides the stated goal
    this.matrix = {
      'tummy|backless': ['underbust', 'tummy'],
      'tummy|*': ['tummy', 'fullbody'],
      'butt|*': ['shorts', 'fullbody'],
      'thighs|*': ['shorts', 'fullbody'],
      'back|backless': ['underbust', 'fullbody'],
      'back|*': ['fullbody', 'underbust'],
      'all|backless': ['underbust', 'fullbody'],
      'all|*': ['fullbody', 'tummy'],
    };

    this.goalPhrase = {
      tummy: 'tummy control', butt: 'butt & hip lift', thighs: 'thigh smoothing',
      back: 'back & bra-line smoothing', all: 'all-over sculpting',
    };
    this.outfitPhrase = {
      dress: 'under a fitted dress', backless: 'under something low-back or backless',
      trousers: 'under trousers or jeans', skirt: 'under a skirt or looser dress',
      everyday: 'for everyday wear',
    };
    this.sizeNote = {
      xs: "Based on US 0–6, you're likely an XS or S — confirm on the chart.",
      m: "Based on US 8–12, you're likely an M or L — confirm on the chart.",
      xl: "Based on US 14–20, you're likely an XL or XXL — confirm on the chart.",
    };

    this.answers = {};
    this.step = 1;
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.result = this.querySelector('[data-result]');
    this.matching = this.querySelector('[data-matching]');
    this.fill = this.querySelector('[data-progress-fill]');
    this.progressLabel = this.querySelector('[data-progress-label]');
    this.back = this.querySelector('[data-back]');

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-q]');
      if (btn) {
        this.answers[Number(btn.dataset.q)] = btn.dataset.v;
        const next = Number(btn.dataset.q) + 1;
        if (next > 3) this.match();
        else this.show(next);
        return;
      }
      if (e.target.closest('[data-restart]')) this.restart();
      if (e.target.closest('[data-back]')) this.goBack();
    });

    // JS is running: reveal the quiz above the always-visible content
    this.quiz.hidden = false;
    this.show(1);
  }

  show(n) {
    this.step = n;
    this.result.hidden = true;
    if (this.matching) this.matching.hidden = true;
    this.steps.forEach((f) => {
      const isActive = Number(f.dataset.step) === n;
      f.hidden = !isActive;
      if (isActive) {
        f.classList.remove('is-in');
        // reflow then animate in
        void f.offsetWidth;
        f.classList.add('is-in');
      }
    });
    this.setProgress(n);
    if (this.back) this.back.hidden = n <= 1;
    const active = this.steps.find((f) => Number(f.dataset.step) === n);
    const first = active && active.querySelector('button');
    if (first) first.focus();
  }

  setProgress(n) {
    if (this.fill) this.fill.style.width = Math.round((n / 3) * 100) + '%';
    if (this.progressLabel) this.progressLabel.textContent = 'Step ' + n + ' of 3';
  }

  match() {
    // brief "finding your match" moment for delight, then reveal
    this.steps.forEach((f) => (f.hidden = true));
    this.setProgress(3);
    if (this.back) this.back.hidden = true;
    if (this.matching) {
      this.matching.hidden = false;
      window.setTimeout(() => {
        this.matching.hidden = true;
        this.resolve();
      }, 750);
    } else {
      this.resolve();
    }
  }

  resolve() {
    const key = this.answers[1] + '|' + this.answers[2];
    const pick = this.matrix[key] || this.matrix[this.answers[1] + '|*'] || ['fullbody', 'tummy'];
    const main = this.products[pick[0]] || {};
    const alt = this.products[pick[1]] || {};

    this.setText('[data-result-name]', main.name || '');
    this.setText('[data-result-why]', this.why[pick[0]] || '');
    this.setImg('[data-result-img]', main.img, main.name);
    const cta = this.querySelector('[data-result-cta]');
    if (cta) cta.href = main.url || '#';

    // personal recap
    const g = this.goalPhrase[this.answers[1]];
    const o = this.outfitPhrase[this.answers[2]];
    this.setText('[data-recap]', g && o ? 'Based on your answers — ' + g + ', ' + o + '.' : '');

    // alternate
    const altWrap = this.querySelector('[data-alt]');
    if (alt.name) {
      this.setText('[data-alt-name]', alt.name);
      const altLink = this.querySelector('[data-alt-link]');
      if (altLink) altLink.href = alt.url || '#';
      this.setImg('[data-alt-img]', alt.img, alt.name);
      if (altWrap) altWrap.hidden = false;
    } else if (altWrap) {
      altWrap.hidden = true;
    }

    // size note
    const note = this.querySelector('[data-size-note]');
    const noteText = this.sizeNote[this.answers[3]];
    if (note) {
      if (noteText) { note.textContent = noteText; note.hidden = false; }
      else { note.hidden = true; }
    }

    this.result.hidden = false;
    this.result.classList.remove('is-in');
    void this.result.offsetWidth;
    this.result.classList.add('is-in');
    this.result.focus();
  }

  goBack() {
    if (this.step > 1) this.show(this.step - 1);
  }

  restart() {
    this.answers = {};
    this.result.hidden = true;
    this.show(1);
  }

  setText(sel, text) {
    const el = this.querySelector(sel);
    if (el) el.textContent = text;
  }

  setImg(sel, src, alt) {
    const el = this.querySelector(sel);
    if (!el) return;
    if (src) {
      el.src = src;
      el.alt = alt || '';
      el.closest('.bloom-ffg__result-media, .bloom-ffg__alt-media')?.classList.remove('is-empty');
    } else {
      el.closest('.bloom-ffg__result-media, .bloom-ffg__alt-media')?.classList.add('is-empty');
    }
  }
}

if (!customElements.get('bloom-fit-guide')) {
  customElements.define('bloom-fit-guide', BloomFitGuide);
}
